import { UtilityService } from '../utility.service';
import { BannerListenerService } from './banner-listener.service';
import CmpIABService from '../cmp-iab-service';
import Constants from '../../constants';
import { ConsentsStatusService } from '../consents-services/consents-status.service';
import { DataService } from './data.service';
import { GetConsentsService } from '../consents-services/get-consents.service';
import { CMPCommands } from '../../Enums/CMPCommands.enums';
import CmpJSApi from '../cmp-js-api';
const SHA256 = require('sha256');
const _ = require('lodash');

const pjson = require('../../../../package.json');

declare var HEADLESS;

/**
 * This class provide window.usercentrics functions.
 */
export class WindowUsercentricsService {
    private _utilityService: UtilityService;

    constructor(utilityService: UtilityService) {
        this._utilityService = utilityService;

        // Initializes all functions available on the window object
        this.windowInteractionCommands();
        this.deprecatedToUsercentrics();
        this.cmpIABServiceWindowInteractionCommands();
        this.updateVersions();
        this.userInformation();
    }

    get utilityService() {
        return this._utilityService;
    }

    /**
     * This function is used to create functions to get the versions of the app, the settings.
     */
    updateVersions() {
        window[Constants.USERCENTRICS_STRING].settingsVersion = this.utilityService.settingsService.settings.version;
        window[Constants.USERCENTRICS_STRING].appVersion = pjson.version;
    }

    userInformation() {
        interface GetUserCountryResponse {
            countryCode: string,
            countryName: string
        }
        const EUCountries = Constants.EU_COUNTRIES;

        window[Constants.USERCENTRICS_STRING].getUserCountry = async () => {
            const response: GetUserCountryResponse = await this.utilityService.apiService.getClientLocation();
            return response;
        };

        window[Constants.USERCENTRICS_STRING].isEUUser = async () => {
            const response: GetUserCountryResponse = await this.utilityService.apiService.getClientLocation();
            return EUCountries.indexOf(response.countryCode) !== -1;
        };
    }

    static updateFromSetting(settings) {
        if (typeof settings.version === 'undefined') {
            throw new Error('version is not defined');
        }
        if (typeof settings.settingsId === 'undefined') {
            throw new Error('settingsId is not defined');
        }
        window[Constants.USERCENTRICS_STRING].settingsVersion = settings.version;
        window[Constants.USERCENTRICS_STRING].settingsId = settings.settingsId;
    }

    /**
     * This function is used to initialize cmp IAB functions.
     */
    cmpIABServiceWindowInteractionCommands() {
        window[Constants.USERCENTRICS_STRING].turnOnIAB = () => {
            this.utilityService.cmpIABService.turnOnIABService();
        };

        window[Constants.USERCENTRICS_STRING].turnOffIAB = () => {
            this.utilityService.cmpIABService.turnOffIabService();
            CmpIABService.deleteFirstPartyCookie();
        };

        window[Constants.USERCENTRICS_STRING].getPurposes = async (id, language) => {
            return await this.utilityService.cmpIABService.getPurposes(id, language);
        };
    }

    /**
     * @Dave
     * window events for interacting with our button
     */
    windowInteractionCommands() {
        if (!HEADLESS) {
            this.initUiInteractionMethods();
        }
        this.initConsentInteractionMethods();
        WindowUsercentricsService.initIABMethods();

        window.usercentrics.toggleDevelopmentMode = () => {
            return this._utilityService.toggleDevelopmentMode();
        };
        window.usercentrics.updateLog = () => {
            this._utilityService.tagLoggerService.updateLog();
        };
        window.usercentrics.updateLanguage = (lang) => {
            this._utilityService.languageService.setLanguage(lang.toLowerCase());
        };
        window.usercentrics.acceptAllConsents = () => {
            if (!this._utilityService.isConsentChangeAllow) {
                return;
            }
            const action = 'onWindowFunctionAcceptAllConsents';
            const updatedBy = 'explicit';
            this._utilityService.saveConsentsService.acceptAllConsents(action, updatedBy);
        };
        window.usercentrics.denyAllConsents = () => {
            if (!this._utilityService.isConsentChangeAllow) {
                return;
            }
            this._utilityService.settingsService.denyAllCookiesExceptEssential();
        };
        window.usercentrics.acceptAllConsentsAndCloseInitialView = () => {
            if (!this._utilityService.isConsentChangeAllow) {
                return;
            }
            this._utilityService.settingsService.saveFirstUserInteraction();
            const action = 'onWindowFunctionAcceptAllConsentsAndCloseInitialView';
            const updatedBy = 'explicit';
            this._utilityService.saveConsentsService.acceptAllConsents(action, updatedBy);
            this._utilityService.settingsService.bannerIsVisible = false;
            this._utilityService.UserCentricViewController.updateView();
        };
        window.usercentrics.denyAllConsentsAndCloseInitialView = () => {
            if (!this._utilityService.isConsentChangeAllow) {
                return;
            }
            this._utilityService.settingsService.saveFirstUserInteraction();
            this._utilityService.settingsService.denyAllCookiesExceptEssential();
            this._utilityService.settingsService.bannerIsVisible = false;
            this._utilityService.UserCentricViewController.updateView();
        };

        window.usercentrics.getProcessorNamesAndIabIdsThatAreCurrentlyTrue = () => {
            return DataService.consents.filter(e => {
                return (
                    e.consentStatus
                );
            }).map(e => {
                return {
                    iabId: DataService.consentTemplates[e.templateId][e.version].iabId,
                    processorName: DataService.consentTemplates[e.templateId][e.version].dataProcessors
                };
            });
        };

        window.usercentrics.getAllConsentsGivenStatus = () => {
            /**
             * return this.utilityService.consentsService.allStatuses === undefined
             * ? false
             * : this.utilityService.consentsService.allStatuses;
             */
            return ConsentsStatusService.selectAllStatus.status;
        };

        window.usercentrics.getBannerHeight = () => {
            return BannerListenerService.bannerHeight;
        };

        window.usercentrics.getBannerDiff = () => {
            return BannerListenerService.bannerDiff;
        };

        if (!window.usercentrics.onViewInit) {
            window.usercentrics.onViewInit = () => {
                // empty block
            };
        }
    }

    public initConsentHistory() {
        window.usercentrics.getConsentsHistory = async () => {
            const paramsToSave: any = {
                operationName: 'getConsents',
                query: `
                    query getConsents(
                      $consentIds: [String]
                    ) {
                      getConsents(
                        consentIds: $consentIds
                      ) {
                        data {
                          consentId
                          action
                          appVersion
                          processorId
                          controllerId
                          createdAt
                          settingsId
                          settingsVersion
                          language
                          consentStatus
                          consentTemplateId
                          consentTemplateVersion
                          updatedBy
                          deviceAuditId
                        }
                        errors
                      }
                    }
                  `,
                variables: {
                    consentIds: DataService.consents.map(e => {
                        return WindowUsercentricsService.getConsentId(e);
                    })
                }
            };
            const response = await this.utilityService.apiService.getConsents(paramsToSave);
            if (response.data && response.data.getConsents && response.data.getConsents.data) {
                // let output = response.data.getConsents.data.map((e) => { return e[0] });

                // return output;
                const output = {};
                response.data.getConsents.data.forEach(element => {
                    output[element[0].consentId] = element;
                });
                return output;
            }
            return DataService.consents.map(e => {
                return {
                    consentId: WindowUsercentricsService.getConsentId(e),
                    processorId: e.processorId,
                    controllerId: e.controllerId,
                    settingsId: DataService.settings.settingsId,
                    settingsVersion: e.history && e.history.length ? e.history[0].settingsVersion : DataService.settings.settingsVersion,
                    language: e.history && e.history.length ? e.history[0].language : DataService.settings.language,
                    consentStatus: e.consentStatus,
                    processor: DataService.consentTemplates[e.templateId][e.version].dataProcessors
                };
            });
        };
    }

    public initConsentInteractionMethods() {
        window.usercentrics.getConsents = (templateId?) => {
            const removedFields = [
                'selectedUndefined',
                '_id',
                'showToggle',
                'showHistory',
                'shortid',
                'selectedUndefined',
                'isVisible',
                'isShared',
                'isLatest',
                'index',
                'id',
                'domAttribute',
                'domAttributes',
                'domElements',
                'domKeys',
                'dataScopesHasData',
                'dataPurposesHasData',
                'dataAttributesHasData',
                'categorySlug',
                'adminSettingsId',
                'subConsentsActive',
                'subConsentsHasData'
            ];
            if (templateId) {
                const consent = _.find(DataService.consents, { templateId: templateId });
                if (!consent) {
                    throw new Error('Consent for template ' + templateId + ' not found');
                }
                const consentTemplate = DataService.consentTemplates[consent.templateId][consent.version];
                const output = {};
                if (consentTemplate) {
                    for (const key in consentTemplate) {
                        if (removedFields.indexOf(key) === -1) {
                            output[key] = consentTemplate[key];
                        }
                    }
                    for (const key in consent) {
                        if (removedFields.indexOf(key) === -1) {
                            output[key] = consent[key];
                        }
                    }
                }
                return output;
            }
            const returnConsents = DataService.consents.map(consent => {
                const output: any = {};
                const consentTemplate = DataService.consentTemplates[consent.templateId][consent.version];

                // Specifically set dataProcessors first
                output.dataProcessingService = consentTemplate.dataProcessors[0];

                for (const key in consentTemplate) {
                    if (removedFields.indexOf(key) === -1) {
                        output[key] = consentTemplate[key];
                    }
                }
                for (const key in consent) {
                    if (removedFields.indexOf(key) === -1) {
                        output[key] = consent[key];
                    }
                }
                output.consentId = GetConsentsService.getConsentId(consent);
                return output;
            });

                // Sort by dataprocessors
            returnConsents.sort((a, b) => {
                var nameA = a.dataProcessors[0].toUpperCase();
                var nameB = b.dataProcessors[0].toUpperCase();
                if (nameA < nameB) {
                  return -1;
                }
                if (nameA > nameB) {
                  return 1;
                }
            });
            return returnConsents;
        };

        window.usercentrics.updateConsents = (consentArray): string => {
            if (!consentArray || !consentArray.length) {
                return '';
            }
            consentArray.forEach(element => {
                const consent = GetConsentsService.getAppropriateConsent(element.templateId);
                ConsentsStatusService.setUnsavedConsentStatus(consent, element.status);

            });
            const action = 'onWindowFunctionUpdateConsent';
            const updatedBy = 'explicit';
            const cdcsDataPulledFromIframe = false;
            this.utilityService.saveConsentsService.saveConsents(action, updatedBy);
            this.utilityService.settingsService.saveFirstUserInteraction();
            return 'Your consent(s) will be updated';
        };
    }

    /**
     *
     */
    public initUiInteractionMethods() {
        window.usercentrics.updatePrivacyButtonIsVisible = (status: boolean) => {
            this._utilityService.settingsService.updateButtonVisibility(status, true);
            this._utilityService.UserCentricViewController.updateView();
        };
        window.usercentrics.togglePrivacyButtonIsVisible = () => {
            this._utilityService.settingsService.updateButtonVisibility(
                !this._utilityService.settingsService.elementsVisibility.primaryButton,
                true
            );
            this._utilityService.UserCentricViewController.updateView();
        };
        window.usercentrics.updateChipIsVisible = (status: boolean) => {
            this._utilityService.settingsService.chipIsVisible = status;
            this._utilityService.UserCentricViewController.updateView();
        };
        window.usercentrics.toggleChipIsVisible = () => {
            this._utilityService.settingsService.chipIsVisible = !this._utilityService.settingsService
                .elementsVisibility.chipButton;
            this._utilityService.UserCentricViewController.updateView();
        };
        window.usercentrics.updateBannerIsVisible = (status: boolean) => {
            this._utilityService.settingsService.bannerIsVisible = status;
            this._utilityService.UserCentricViewController.updateView();
        };
        window.usercentrics.toggleBannerIsVisible = () => {
            this._utilityService.settingsService.bannerIsVisible = !this._utilityService.settingsService
                .elementsVisibility.bannerVisible;
            this._utilityService.UserCentricViewController.updateView();
        };
        window.usercentrics.updateCornerModalIsVisible = (status: boolean) => {
            this._utilityService.UserCentricViewController.privacyCornerModal.visible = status;
            this._utilityService.UserCentricViewController.updateView();
        };
        window.usercentrics.toggleCornerModalIsVisible = () => {
            this._utilityService.UserCentricViewController.privacyCornerModal.visible = !this._utilityService
                .settingsService.elementsVisibility.cornerModel;
            this._utilityService.UserCentricViewController.updateView();
        };
        window.usercentrics.updateCenteredModalIsVisible = (status: boolean) => {
            this._utilityService.UserCentricViewController.privacyCenterModal.visible = status;
            this._utilityService.UserCentricViewController.updateView();
        };
        window.usercentrics.toggleCenteredModalIsVisible = () => {
            this._utilityService.UserCentricViewController.privacyCenterModal.visible = !this._utilityService
                .settingsService.elementsVisibility.centerModel;
            this._utilityService.UserCentricViewController.updateView();
        };
        window.usercentrics.updateConsentInfoModalIsVisible = (status: boolean, id?: string) => {
            if (status) {
                this.utilityService.UserCentricViewController.openConsentInfoModal(id);
            } else {
                this.utilityService.UserCentricViewController.closeConsentInfoModal();
            }
        };
        window.usercentrics.toggleConsentInfoModalIsVisible = (id?) => {
            if (!this.utilityService.UserCentricViewController.consentsInfoModal.visible) {
                this.utilityService.UserCentricViewController.openConsentInfoModal(id);
            } else {
                this.utilityService.UserCentricViewController.closeConsentInfoModal();
            }
        };
    }

    
    /**
     * Methods for interacting with usercentric specific implementations
     * regarding IAB.
     */
    public static initIABMethods() {
        window[Constants.USERCENTRICS_STRING].iab = {
            [CMPCommands.DECODE_CONSENT_STRING]: (consentString: string) => {
                return CmpIABService.decodeConsentString(consentString);
            },
            [CMPCommands.GET_CONSENT_DATA]: (consentStringVersion: string) => {
                return CmpJSApi.getConsentDataString(consentStringVersion);
            }
        };
    }
    
    static getConsentId(_consent) {
        const consentId = SHA256(
            DataService.settings.settingsId +
                '_' +
                JSON.stringify([_consent.controllerId]) +
                '_' +
                JSON.stringify([_consent.processorId])
        );
        return consentId; // .toString();
    }

    deprecatedToUsercentrics() {
        window.toUsercentrics = window.toUsercentrics || {};
        setInterval(() => {
            const e = window.toUsercentrics;
            if (e) {
                for (const i in e) {
                    if (i === 'buttonIsVisible') {
                        if (typeof e[i] === 'boolean') {
                            // this._utilityService.settingsService.privacyButtonIsVisible =

                            //   e[i];
                            this._utilityService.settingsService.updateButtonVisibility(e[i], true);
                            this._utilityService.UserCentricViewController.updateView();
                        }
                        delete e[i];
                    }
                }
            }
        }, 2000);
    }
}
